import type { Permission } from "@l2a/shared";
import type { ReactNode } from "react";
import { useAuth } from "@/app/auth/session";

export type PermissionCheck = Permission | readonly Permission[];

/** True when every permission in `check` is granted (`mode: "any"` → at least one). */
export function hasPermissions(granted: ReadonlySet<string>, check: PermissionCheck | undefined, mode: "all" | "any" = "all"): boolean {
  if (!check) return true;
  const list = typeof check === "string" ? [check] : check;
  if (list.length === 0) return true;
  return mode === "all" ? list.every((p) => granted.has(p)) : list.some((p) => granted.has(p));
}

/**
 * UI gating only — the API enforces every permission server-side.
 *   const canEdit = usePermission("clients:write");
 *   const canSeeMoney = usePermission(["financials:view", "invoices:read"], "any");
 */
export function usePermission(check: PermissionCheck | undefined, mode: "all" | "any" = "all"): boolean {
  const { permissions } = useAuth();
  return hasPermissions(permissions, check, mode);
}

/** Returns a checker bound to the current session (for filtering lists of items). */
export function usePermissionChecker(): (check: PermissionCheck | undefined, mode?: "all" | "any") => boolean {
  const { permissions } = useAuth();
  return (check, mode = "all") => hasPermissions(permissions, check, mode);
}

/**
 *   <Can perm="invoices:send"><Button>Send</Button></Can>
 *   <Can perm={["leads:write","leads:convert"]} mode="any" fallback={<ReadOnlyBadge/>}>…</Can>
 */
export function Can({ perm, mode = "all", fallback = null, children }: { perm: PermissionCheck; mode?: "all" | "any"; fallback?: ReactNode; children: ReactNode }) {
  return usePermission(perm, mode) ? <>{children}</> : <>{fallback}</>;
}
